import { routes } from "../routes";
import { pathWithProject } from "./project-id";

export type ModuleGroupId = "trends" | "content" | "data";

export type ModuleId =
  | "content-decay"
  | "traffic-trend"
  | "rank-tracking"
  | "traffic-insights"
  | "api-scheduler"
  | "performance-insights"
  | "performance-details"
  | "optimization-wizard"
  | "keyword-map"
  | "content-optimization"
  | "url-inspection"
  | "ad-hoc-export";

export type ModuleNavItem = {
  id: ModuleId;
  label: string;
  group: ModuleGroupId;
  paidOnly: boolean;
  /** Shown only when the account has at least two projects. */
  requiresMultipleProjects?: boolean;
  /** Path fragment that marks every page of this module active. */
  match: string;
};

export const MODULE_GROUPS: Array<{ id: ModuleGroupId; label: string }> = [
  { id: "trends", label: "Trends" },
  { id: "content", label: "Content" },
  { id: "data", label: "Data" },
];

// Discovery reports come before the editor. Data tools stay in their own group.
export const MODULE_NAV: ModuleNavItem[] = [
  {
    id: "content-decay",
    label: "Content Decay",
    group: "trends",
    paidOnly: false,
    match: "/content-decay/",
  },
  {
    id: "traffic-trend",
    label: "Traffic Trend",
    group: "trends",
    paidOnly: false,
    match: "/traffic-trend/",
  },
  {
    id: "rank-tracking",
    label: "Rank Tracking",
    group: "trends",
    paidOnly: false,
    match: "/rank-tracking/",
  },
  {
    id: "traffic-insights",
    label: "Traffic Insights",
    group: "trends",
    paidOnly: true,
    match: "/traffic-insights/",
  },
  {
    id: "api-scheduler",
    label: "API Scheduler",
    group: "trends",
    paidOnly: false,
    match: "/api-schedule/",
  },
  {
    id: "performance-insights",
    label: "Performance Insights",
    group: "trends",
    paidOnly: false,
    requiresMultipleProjects: true,
    match: "/performance-insights/",
  },
  {
    id: "performance-details",
    label: "Performance Details",
    group: "trends",
    paidOnly: false,
    match: "/performance-details/",
  },
  {
    id: "optimization-wizard",
    label: "Optimization Wizard",
    group: "content",
    paidOnly: false,
    match: "/optimization-wizard/",
  },
  {
    id: "keyword-map",
    label: "Keyword Map",
    group: "content",
    paidOnly: true,
    match: "/keyword-map/",
  },
  {
    id: "content-optimization",
    label: "Content Optimization",
    group: "content",
    paidOnly: false,
    match: "/content-optimization/",
  },
  {
    id: "url-inspection",
    label: "URL Inspection",
    group: "data",
    paidOnly: false,
    match: "/url-inspection/",
  },
  {
    id: "ad-hoc-export",
    label: "Ad Hoc Export",
    group: "data",
    paidOnly: false,
    match: "/adhoc-exports/",
  },
];

export function visibleModules(isPaid: boolean): ModuleNavItem[] {
  return MODULE_NAV.filter((item) => isPaid || !item.paidOnly);
}

export function modulesForGroup(
  group: ModuleGroupId,
  isPaid: boolean,
  projectCount = 0
): ModuleNavItem[] {
  return visibleModules(isPaid).filter((item) => {
    if (item.group !== group) {
      return false;
    }
    if (item.requiresMultipleProjects && projectCount < 2) {
      return false;
    }
    return true;
  });
}

export function moduleForPath(pathname: string, isPaid: boolean): ModuleNavItem | undefined {
  return visibleModules(isPaid).find((item) => pathname.includes(item.match));
}

export function moduleHref(
  id: ModuleId,
  projectId: string | null | undefined,
  keywordMapHref: string
): string {
  switch (id) {
    case "content-decay":
      return pathWithProject(routes.listContentDecay.path, projectId);
    case "traffic-trend":
      return pathWithProject(routes.trafficTrend.path, projectId);
    case "rank-tracking":
      return pathWithProject(routes.rankTracking.path, projectId);
    case "traffic-insights":
      return pathWithProject(routes.listTrafficInsights.path, projectId);
    case "api-scheduler":
      return pathWithProject(routes.listAPISchedule.path, projectId);
    case "performance-insights":
      return pathWithProject(routes.performanceInsights.path, projectId);
    case "performance-details":
      return pathWithProject(routes.performanceDetails.path, projectId);
    case "optimization-wizard":
      return pathWithProject(routes.listOptimizationWizard.path, projectId);
    case "keyword-map":
      return keywordMapHref;
    case "content-optimization":
      return pathWithProject(routes.listContentOptimization.path, projectId);
    case "url-inspection":
      return pathWithProject(routes.urlInspection.path, projectId);
    case "ad-hoc-export":
      return pathWithProject(routes.listAdhocExport.path, projectId);
    default: {
      const unknown: never = id;
      return unknown;
    }
  }
}
